"use client";

import { useEffect } from "react";
import { BUILT_IN_FONT_WEIGHTS } from "../lib/builtInFonts";

/**
 * Single shared <link> tag that loads the brand-kit built-in fonts from Google
 * Fonts. Reference-counted: the link is appended on the first mount and removed
 * when the last consumer unmounts, so we don't ship these fonts on routes that
 * never touch the brand-kit feature.
 *
 * Includes `Rubik` even though `next/font` loads it globally — `next/font`
 * scopes the family to a hashed class name, so `font-family: "Rubik"` would
 * otherwise not resolve. The dual-load is cheap and makes the picker reliable.
 */
const GOOGLE_FONTS_HOST = "https://fonts.googleapis.com/css2";

function buildHref(): string {
    const families = Object.entries(BUILT_IN_FONT_WEIGHTS)
        .map(
            ([font, weights]) =>
                `family=${encodeURIComponent(font).replace(/%20/g, "+")}:wght@${weights.join(";")}`
        )
        .join("&");
    return `${GOOGLE_FONTS_HOST}?${families}&display=swap`;
}

const LINK_DATA_ATTR = "data-brand-kits-fonts";
let refCount = 0;
let linkEl: HTMLLinkElement | null = null;

export function useBuiltInFontsLoader(): void {
    useEffect(() => {
        if (typeof document === "undefined") return;

        refCount += 1;
        if (!linkEl) {
            // If a previous mount left one behind (HMR), reuse it.
            const existing = document.querySelector<HTMLLinkElement>(
                `link[${LINK_DATA_ATTR}]`
            );
            if (existing) {
                linkEl = existing;
            } else {
                linkEl = document.createElement("link");
                linkEl.rel = "stylesheet";
                linkEl.setAttribute(LINK_DATA_ATTR, "true");
                linkEl.href = buildHref();
                document.head.appendChild(linkEl);
            }
        }

        return () => {
            refCount -= 1;
            if (refCount <= 0 && linkEl) {
                linkEl.remove();
                linkEl = null;
                refCount = 0;
            }
        };
    }, []);
}
